import { useState, useRef, useEffect, useMemo } from 'react'
import { Play, Pause, SkipBack, SkipForward, Calendar, X, Download } from 'lucide-react'
import { useApp } from '../../store/AppContext'
import { fetchHistoricalData, fetchHistoryBatch, fetchDataSessions } from '../../firebase/boatData'
import { format } from 'date-fns'
import { buildBoatCsv } from '../../playback/csvExport'
import { Session, BoatData } from '../../types'

/**
 * The replay timeline: a fleet-activity histogram you can scrub, modelled on the coach
 * page's.
 *
 * A bare slider says nothing about the run. Bar height here is how much the fleet MOVED
 * in that slice (summed distance, falling back to fix count when a boat sat still), so
 * the shape of the day is legible at a glance: flat stretches are drifting or moored,
 * gaps are dropouts, and the tall middle is the actual sailing. Everything is computed
 * from frames already in memory — no extra request, and it works for a single boat or a
 * whole fleet.
 */
function ActivityTimeline({ startTime, endTime, tCur, framesByBoat, frames, onSeek }: {
    startTime: number; endTime: number; tCur: number
    framesByBoat?: Record<string, BoatData[]>
    frames: BoatData[]
    onSeek: (t: number) => void
}) {
    const ref = useRef<HTMLCanvasElement | null>(null)
    const span = Math.max(1, endTime - startTime)
    const BINS = 160

    // Bin the fleet's movement once per data change, not per frame of playback.
    const bins = useMemo(() => {
        const out = new Float64Array(BINS)
        const tracks = framesByBoat && Object.keys(framesByBoat).length
            ? Object.values(framesByBoat) : [frames]
        for (const fr of tracks) {
            let prev: any = null
            for (const f of fr) {
                const i = Math.min(BINS - 1, Math.max(0, Math.floor(((f.timestamp - startTime) / span) * BINS)))
                const la = f.parsed?.lat, lo = f.parsed?.lon
                if (prev && la != null && lo != null && prev.lat != null) {
                    // Cheap equirectangular metres — exactness is irrelevant for a bar chart.
                    const dx = (lo - prev.lon) * 111320 * Math.cos((la * Math.PI) / 180)
                    const dy = (la - prev.lat) * 111320
                    const d = Math.hypot(dx, dy)
                    out[i] += d < 200 ? d : 0          // ignore teleports; they would dwarf everything
                } else {
                    out[i] += 0.5                       // presence still registers when stationary
                }
                if (la != null && lo != null) prev = { lat: la, lon: lo }
            }
        }
        return out
    }, [framesByBoat, frames, startTime, span])

    useEffect(() => {
        const cv = ref.current
        if (!cv) return
        const w = cv.clientWidth || 320, h = 34
        const dpr = window.devicePixelRatio || 1
        cv.width = w * dpr; cv.height = h * dpr
        const g = cv.getContext('2d')
        if (!g) return
        g.setTransform(dpr, 0, 0, dpr, 0, 0)
        g.clearRect(0, 0, w, h)
        let max = 0
        for (const v of bins) if (v > max) max = v
        const bw = w / BINS
        for (let i = 0; i < BINS; i++) {
            const v = max > 0 ? bins[i] / max : 0
            const bh = v > 0 ? Math.max(1.5, v * (h - 6)) : 0
            if (!bh) continue
            // Past the playhead is muted; the covered part is bright — the same "where am I"
            // cue the scrubber gives, carried by the chart itself.
            const past = (startTime + ((i + 0.5) / BINS) * span) <= tCur
            g.fillStyle = past ? 'rgba(96,165,250,0.85)' : 'rgba(148,163,184,0.32)'
            g.fillRect(i * bw, h - bh - 2, Math.max(1, bw - 0.6), bh)
        }
        const px = ((tCur - startTime) / span) * w
        g.fillStyle = '#f8fafc'
        g.fillRect(Math.max(0, Math.min(w - 1.5, px)), 0, 1.5, h)
    }, [bins, tCur, startTime, span])

    function seekFromEvent(e: React.MouseEvent<HTMLCanvasElement>) {
        const r = e.currentTarget.getBoundingClientRect()
        const frac = Math.min(1, Math.max(0, (e.clientX - r.left) / r.width))
        onSeek(Math.round(startTime + frac * span))
    }

    return (
        <canvas
            ref={ref}
            onClick={seekFromEvent}
            onMouseMove={e => { if (e.buttons === 1) seekFromEvent(e) }}
            title="Fleet activity over the run — click or drag to jump to a moment"
            style={{ width: '100%', height: 34, cursor: 'pointer', display: 'block' }}
        />
    )
}

export default function PlaybackControls() {
    const { playbackState, setPlaybackState, selectedBoats, currentSchool, t } = useApp()
    const [showDatePicker, setShowDatePicker] = useState(false)
    const [startDate, setStartDate] = useState('')
    const [endDate, setEndDate] = useState('')
    const [loading, setLoading] = useState(false)
    // CSV download of a requested range (docs/105): extracted from the binary history for exactly that range, then converted
    const [showExport, setShowExport] = useState(false)
    const [exportFrom, setExportFrom] = useState('')
    const [exportTo, setExportTo] = useState('')
    const [exporting, setExporting] = useState(false)
    const [sessions, setSessions] = useState<Session[]>([])
    const [loadingSessions, setLoadingSessions] = useState(false)

    // Load sessions when date picker opens
    useState(() => {
        if (showDatePicker && selectedBoats.length > 0) {
            loadSessions()
        }
    })

    // Use effect to reload sessions when picker opens
    const [prevShowDatePicker, setPrevShowDatePicker] = useState(false)
    if (showDatePicker !== prevShowDatePicker) {
        setPrevShowDatePicker(showDatePicker)
        if (showDatePicker && selectedBoats.length > 0) {
            loadSessions()
        }
    }

    async function loadSessions() {
        if (!selectedBoats[0]) return
        setLoadingSessions(true)
        const data = await fetchDataSessions(selectedBoats[0])
        setSessions(data)
        setLoadingSessions(false)
    }

    // The server session picked from the list — its id goes with the history request while the range is untouched.
    const [pickedSession, setPickedSession] = useState<Session | null>(null)

    function selectSession(session: Session) {
        setPickedSession(session.name !== undefined ? session : null)
        // Format for datetime-local input: YYYY-MM-DDThh:mm:ss
        // We need to handle timezone offset manually for datetime-local
        const toLocalISO = (ts: number) => {
            const date = new Date(ts)
            const offset = date.getTimezoneOffset() * 60000
            return new Date(date.getTime() - offset).toISOString().slice(0, 19)
        }

        setStartDate(toLocalISO(session.startTime))
        setEndDate(toLocalISO(session.endTime))
    }

    async function loadPlaybackData() {
        if (!currentSchool || selectedBoats.length === 0) return
        if (!startDate || !endDate) {
            alert('Please select start and end dates')
            return
        }

        setLoading(true)

        try {
            const start = new Date(startDate).getTime()
            const end = new Date(endDate).getTime()
            // the picked session still matches the range (to the second the inputs keep) -> ask by its id
            const sid = pickedSession && Math.abs(Math.floor(pickedSession.startTime / 1000) - Math.floor(start / 1000)) <= 1
                && Math.abs(Math.floor(pickedSession.endTime / 1000) - Math.floor(end / 1000)) <= 1 ? pickedSession.id : undefined

            // Fleet replay: fetch EVERY selected boat in parallel — and BOTH position sources
            // at once, as layers. Phone GPS (Firebase, carries the sensors) drives frames/
            // sensors/CSV; the server-recalced PPK track rides alongside in ppkByBoat.
            // Show/hide is then pure rendering — no re-fetch, no timeline reset (the old
            // behaviour re-loaded on the source switch and threw the playhead back to 0).
            // One batched binary request for every boat and both layers (docs/105); null -> the per-boat requests below.
            const batch = await fetchHistoryBatch(selectedBoats, start, end, sid)
            const results = await Promise.all(
                selectedBoats.map(async b => {
                    const got = batch?.[b]
                    if (got && got.phone.length) return [b, got.phone, got.ppk] as const
                    // the batch failed, or our server has no phone track for this boat: ask per boat, as before (Firebase fallback inside)
                    return [b,
                        await fetchHistoricalData(b, start, end, undefined, b === selectedBoats[0] ? sid : undefined),   // our server first, Firebase fallback
                        got ? got.ppk : await fetchHistoricalData(b, start, end, 'ppk', b === selectedBoats[0] ? sid : undefined)] as const
                }))
            const framesByBoat: Record<string, any[]> = {}
            const ppkByBoat: Record<string, any[]> = {}
            for (const [b, phone, ppk] of results) {
                // A boat may exist in only one store — keep whichever has data so the
                // marker always has something to follow.
                if (phone.length) framesByBoat[b] = phone
                else if (ppk.length) framesByBoat[b] = ppk
                if (ppk.length) ppkByBoat[b] = ppk
            }
            const frames = framesByBoat[selectedBoats[0]] ?? Object.values(framesByBoat)[0] ?? []

            if (frames.length === 0) {
                alert('No data found for selected date range')
                setLoading(false)
                return
            }

            setPlaybackState({
                frames,
                framesByBoat,
                ppkByBoat,
                currentIndex: 0,
                startTime: start,
                endTime: end,
                tCur: start,
                isPlaying: false
            })

            setShowDatePicker(false)
            setLoading(false)
        } catch (error) {
            console.error('Failed to load playback data:', error)
            alert('Failed to load playback data')
            setLoading(false)
        }
    }

    function togglePlayback() {
        if (!playbackState.frames.length) {
            setShowDatePicker(true)
            return
        }

        setPlaybackState({ isPlaying: !playbackState.isPlaying })

        if (!playbackState.isPlaying) {
            startPlayback()
        }
    }

    /** frames index of the last record at or before t — keeps trail/CSV in step with the playhead. */
    function indexAt(frames: any[], t: number): number {
        let i = 0
        while (i + 1 < frames.length && frames[i + 1].timestamp <= t) i++
        return i
    }

    function startPlayback() {
        // Wall-clock playback: advance the playhead through REAL time at `speed`×, so the
        // whole fleet stays in the same moment together. Index-stepping (1 frame/tick)
        // compressed every gap and stretched every burst — fine for one boat, wrong for two.
        const TICK = 250
        const interval = setInterval(() => {
            setPlaybackState((prev: any) => {
                if (!prev.isPlaying) {
                    clearInterval(interval)
                    return prev
                }
                const end = prev.endTime ?? 0
                const t = Math.min((prev.tCur ?? prev.startTime ?? 0) + TICK * prev.speed, end)
                const done = t >= end
                if (done) clearInterval(interval)
                return { ...prev, tCur: t, currentIndex: indexAt(prev.frames, t), isPlaying: !done }
            })
        }, TICK)
    }

    function handleSliderChange(t: number) {
        setPlaybackState((prev: any) => ({ tCur: t, currentIndex: indexAt(prev.frames, t) }))
    }

    function clearPlayback() {
        setPlaybackState({
            isPlaying: false,
            currentIndex: 0,
            frames: [],
            framesByBoat: undefined,
            tCur: null,
            startTime: null,
            endTime: null
        })
    }

    const toLocalInput = (ts: number) => new Date(ts - new Date(ts).getTimezoneOffset() * 60000).toISOString().slice(0, 19)

    function openExport() {
        if (!playbackState.frames.length) return
        setExportFrom(toLocalInput(playbackState.startTime ?? playbackState.frames[0].timestamp))
        setExportTo(toLocalInput(playbackState.endTime ?? playbackState.frames[playbackState.frames.length - 1].timestamp))
        setShowExport(true)
    }

    /** Download the requested range as CSV: the range is pulled from the server's binary history (exact float64 columns, so the
     *  rows are bit-identical to the JSON endpoint's) and converted to CSV only now. When the server cannot be reached it falls
     *  back to the frames already in memory, cut to the range. */
    async function downloadCsv() {
        const boat = selectedBoats[0]
        const from = new Date(exportFrom).getTime(), to = new Date(exportTo).getTime()
        if (!boat || !(to > from)) { alert('Pick a start before the end'); return }
        setExporting(true)
        try {
            const sid = pickedSession && from >= pickedSession.startTime - 1000 && to <= pickedSession.endTime + 1000 ? pickedSession.id : undefined
            const got = (await fetchHistoryBatch([boat], from, to, sid, { exact: true }))?.[boat]
            let frames: BoatData[], ppk: BoatData[]
            if (got && got.phone.length) { frames = got.phone; ppk = got.ppk }
            else {
                const within = (f: BoatData) => f.timestamp >= from && f.timestamp <= to
                frames = playbackState.frames.filter(within)
                ppk = (playbackState.ppkByBoat?.[boat] ?? []).filter(within)
            }
            if (!frames.length) { alert('No data in that range'); return }
            const blob = new Blob([buildBoatCsv(frames, ppk)], { type: 'text/csv;charset=utf-8;' })
            const link = document.createElement('a')
            const url = URL.createObjectURL(blob)
            link.setAttribute('href', url)
            link.setAttribute('download', `boat_data_${boat}_${format(from, 'yyyyMMdd_HHmmss')}-${format(to, 'HHmmss')}.csv`)
            link.style.visibility = 'hidden'
            document.body.appendChild(link)
            link.click()
            document.body.removeChild(link)
            setShowExport(false)
        } finally { setExporting(false) }
    }

    if (!currentSchool || selectedBoats.length === 0) return null

    const currentFrame = playbackState.frames[playbackState.currentIndex]
    const hasData = playbackState.frames.length > 0

    return (
        <>
            {/* Export range modal */}
            {showExport && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="glass-dark rounded-xl p-4 sm:p-6 w-[calc(100%-2rem)] max-w-[420px] shadow-2xl">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-white">{t('playback.export')}</h3>
                            <button onClick={() => setShowExport(false)} className="text-white/60 hover:text-white transition-colors">
                                <X className="w-5 h-5" />
                            </button>
                        </div>
                        <div className="space-y-4">
                            <div>
                                <label className="block text-sm font-medium text-white/80 mb-2">{t('playback.start')}</label>
                                <input type="datetime-local" step="1" value={exportFrom} onChange={(e) => setExportFrom(e.target.value)}
                                    className="input-field bg-white/10 text-white border-white/20 w-full" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-white/80 mb-2">{t('playback.end')}</label>
                                <input type="datetime-local" step="1" value={exportTo} onChange={(e) => setExportTo(e.target.value)}
                                    className="input-field bg-white/10 text-white border-white/20 w-full" />
                            </div>
                            <button onClick={downloadCsv} disabled={exporting} className="btn-primary w-full">
                                {exporting ? t('playback.loading') : t('playback.export')}
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Date Picker Modal */}
            {showDatePicker && (
                <div className="absolute inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
                    <div className="glass-dark rounded-xl p-4 sm:p-6 w-[calc(100%-2rem)] max-w-[600px] shadow-2xl max-h-[90vh] overflow-y-auto">
                        <div className="flex items-center justify-between mb-4">
                            <h3 className="text-lg font-semibold text-white">{t('playback.selectDate')}</h3>
                            <button
                                onClick={() => setShowDatePicker(false)}
                                className="text-white/60 hover:text-white transition-colors"
                            >
                                <X className="w-5 h-5" />
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            {/* Manual Selection */}
                            <div className="space-y-4">
                                <h4 className="text-sm font-medium text-white/60 uppercase tracking-wider">{t('playback.manual')}</h4>
                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-2">
                                        {t('playback.start')}
                                    </label>
                                    <input
                                        type="datetime-local"
                                        step="1"
                                        value={startDate}
                                        onChange={(e) => setStartDate(e.target.value)}
                                        className="input-field bg-white/10 text-white border-white/20 w-full"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-white/80 mb-2">
                                        {t('playback.end')}
                                    </label>
                                    <input
                                        type="datetime-local"
                                        step="1"
                                        value={endDate}
                                        onChange={(e) => setEndDate(e.target.value)}
                                        className="input-field bg-white/10 text-white border-white/20 w-full"
                                    />
                                </div>

                                <button
                                    onClick={loadPlaybackData}
                                    disabled={loading}
                                    className="btn-primary w-full mt-4"
                                >
                                    {loading ? t('playback.loading') : t('playback.load')}
                                </button>
                            </div>

                            {/* Available Sessions */}
                            <div className="border-t pt-6 md:border-t-0 md:pt-0 md:border-l border-white/10 md:pl-6">
                                <h4 className="text-sm font-medium text-white/60 uppercase tracking-wider mb-4">
                                    {t('playback.sessions')}
                                </h4>

                                {loadingSessions ? (
                                    <div className="text-white/40 text-sm">{t('playback.loading')}</div>
                                ) : sessions.length === 0 ? (
                                    <div className="text-white/40 text-sm">{t('playback.noSessions')}</div>
                                ) : (
                                    <div className="space-y-2 max-h-[300px] overflow-y-auto pr-2 custom-scrollbar">
                                        {sessions.map(session => (
                                            <button
                                                key={session.id}
                                                onClick={() => selectSession(session)}
                                                className="w-full text-left p-3 rounded-lg bg-white/5 hover:bg-white/10 transition-all group"
                                            >
                                                <div className="text-sm font-medium text-white group-hover:text-primary-300">
                                                    {format(session.startTime, 'MMM d, HH:mm')}
                                                </div>
                                                {session.name && <div className="text-xs text-white/60 truncate">{session.name}</div>}
                                                <div className="flex justify-between items-center mt-1">
                                                    <span className="text-xs text-white/40">
                                                        {format(session.duration, 'mm')} min
                                                    </span>
                                                    <span className="text-xs text-white/40">
                                                        {session.dataPoints} points
                                                    </span>
                                                </div>
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Playback Bar */}
            <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 z-10 w-[calc(100%-2rem)] max-w-[600px] glass-dark rounded-xl shadow-2xl p-3 sm:p-4">
                <div className="flex flex-col sm:flex-row items-center gap-3 sm:gap-4">
                    {/* Play/Pause Button */}
                    <button
                        onClick={togglePlayback}
                        className="p-3 bg-primary-600 hover:bg-primary-700 rounded-full transition-all shadow-lg active:scale-95"
                    >
                        {playbackState.isPlaying ? (
                            <Pause className="w-5 h-5 text-white" />
                        ) : (
                            <Play className="w-5 h-5 text-white" />
                        )}
                    </button>

                    {hasData ? (
                        <>
                            {/* Timeline — WALL-CLOCK: scrubs real time, not frame indexes, so every
                                boat in a fleet replay sits at the same moment together. The
                                histogram behind it shows when the fleet was actually recording and
                                moving, which is what turns a featureless bar into a map of the day:
                                gaps are dropouts, tall bars are the active stretches. */}
                            <div className="flex-1">
                                <ActivityTimeline
                                    startTime={playbackState.startTime ?? 0}
                                    endTime={playbackState.endTime ?? 1}
                                    tCur={playbackState.tCur ?? playbackState.startTime ?? 0}
                                    framesByBoat={playbackState.framesByBoat}
                                    frames={playbackState.frames}
                                    onSeek={handleSliderChange}
                                />
                                <input
                                    type="range"
                                    min={playbackState.startTime ?? 0}
                                    max={playbackState.endTime ?? 1}
                                    step={1000}
                                    value={playbackState.tCur ?? playbackState.startTime ?? 0}
                                    onChange={(e) => handleSliderChange(parseInt(e.target.value))}
                                    aria-label="Playback position"
                                    className="w-full h-2 bg-white/20 rounded-lg appearance-none cursor-pointer slider"
                                />

                                {/* Time Info */}
                                <div className="flex justify-between mt-2 text-xs text-white/60">
                                    <span>
                                        {format(playbackState.tCur ?? currentFrame?.timestamp ?? Date.now(), 'MMM d, HH:mm:ss')}
                                    </span>
                                    <span>
                                        {playbackState.framesByBoat && Object.keys(playbackState.framesByBoat).length > 1
                                            ? `${Object.keys(playbackState.framesByBoat).length} boats`
                                            : `${playbackState.currentIndex + 1} / ${playbackState.frames.length}`}
                                    </span>
                                </div>
                            </div>

                            {/* Speed Control */}
                            <div className="flex items-center gap-1 sm:gap-2">
                                <button
                                    onClick={() => setPlaybackState({ speed: Math.max(0.5, playbackState.speed - 0.5) })}
                                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all"
                                >
                                    <SkipBack className="w-4 h-4" />
                                </button>
                                <div className="text-white text-sm font-medium w-10 text-center">
                                    {playbackState.speed}x
                                </div>
                                <button
                                    onClick={() => setPlaybackState({ speed: Math.min(5, playbackState.speed + 0.5) })}
                                    className="p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all"
                                >
                                    <SkipForward className="w-4 h-4" />
                                </button>
                            </div>

                            {/* Export / Clear Buttons Row for Mobile */}
                            <div className="flex sm:contents w-full justify-between gap-2 mt-2 sm:mt-0">
                                {/* Export Button */}
                                <button
                                    onClick={openExport}
                                    className="flex-1 sm:flex-none p-2 rounded-lg bg-white/10 hover:bg-white/20 text-white transition-all flex justify-center"
                                    title={t('playback.export')}
                                >
                                    <Download className="w-4 h-4" />
                                </button>

                                {/* Clear Button */}
                                <button
                                    onClick={clearPlayback}
                                    className="flex-1 sm:flex-none p-2 rounded-lg bg-red-500/20 hover:bg-red-500/30 text-red-400 transition-all flex justify-center"
                                >
                                    <X className="w-4 h-4" />
                                </button>
                            </div>
                        </>
                    ) : (
                        <div className="flex-1 flex items-center justify-between">
                            <div className="text-white/60 text-sm">{t('playback.noData')}</div>
                            <button
                                onClick={() => setShowDatePicker(true)}
                                className="btn-secondary flex items-center gap-2"
                            >
                                <Calendar className="w-4 h-4" />
                                {t('playback.selectDate')}
                            </button>
                        </div>
                    )}
                </div>
            </div>

            <style>{`
        .slider::-webkit-slider-thumb {
          appearance: none;
          width: 16px;
          height: 16px;
          border-radius: 50%;
          background: #3b82f6;
          cursor: pointer;
          box-shadow: 0 2px 8px rgba(59, 130, 246, 0.5);
        }
        
        .slider::-webkit-slider-thumb:hover {
          background: #2563eb;
          box-shadow: 0 4px 12px rgba(59, 130, 246, 0.6);
        }
      `}</style>
        </>
    )
}
